import { formatShortMoney } from "@/lib/formatters";
import { useUrlState } from "@/lib/useUrlState";
import { useState, useEffect, useCallback, useMemo } from "react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Pill } from "@/components/FilterPills";
import { Toaster, toast } from "sonner";
import { ContractsTable } from "@/components/ContractsTable";
import { FollowupDialog } from "@/components/FollowupDialog";
import { api } from "@/lib/api";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const CONTRACT_STATUS_OPTIONS = ["Open", "Reviewed", "Negotiating", "Pending CEO Signature", "Pending GC Signature", "Handoff Requested", "Closed"];
const CONTRACT_TYPE_OPTIONS = ["Contract", "LOI"];

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [archivedContracts, setArchivedContracts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useUrlState("view", "active"); // "active" | "archive"
  const [amFilter, setAmFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContract, setNewContract] = useState({
    job_number: "",
    job_name: "",
    contract_type: "Contract",
    received: "",
    status: "Open",
    notes: "",
    account_manager: "",
    customer: "",
    contact_name: "",
    contact_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [followupFor, setFollowupFor] = useState(null);
  const [followupPreview, setFollowupPreview] = useState(null);
  const [followupSending, setFollowupSending] = useState(false);

  const openFollowup = useCallback(async (contract) => {
    setFollowupFor(contract);
    setFollowupPreview(null);
    try {
      const p = await api.previewReminder(contract.id);
      setFollowupPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setFollowupFor(null);
    }
  }, []);

  const sendFollowup = useCallback(async () => {
    if (!followupFor) return;
    setFollowupSending(true);
    try {
      const r = await api.sendReminder(followupFor.id);
      if (r.ok) {
        toast.success(`Follow-up sent to ${followupFor.contact_email || "contact"}`);
        setFollowupFor(null);
        fetchContracts();
      } else {
        toast.error(r.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setFollowupSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupFor]);

  const fetchContracts = useCallback(async () => {
    try {
      const [active, archived] = await Promise.all([
        api.getContracts(false),
        api.getContracts(true),
      ]);
      setContracts(active);
      setArchivedContracts(archived);
    } catch (e) {
      console.error("Failed to fetch contracts:", e);
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const currentContracts = viewMode === "active" ? contracts : archivedContracts;

  const filteredContracts = useMemo(() => {
    let result = currentContracts;
    if (amFilter !== "ALL") {
      result = result.filter((c) => c.account_manager === amFilter);
    }
    if (statusFilter !== "ALL") {
      result = result.filter((c) => c.status === statusFilter);
    }
    return result;
  }, [currentContracts, amFilter, statusFilter]);

  const amOptions = useMemo(() => {
    const ams = new Set();
    currentContracts.forEach((c) => {
      if (c.account_manager && c.account_manager.trim()) ams.add(c.account_manager.trim());
    });
    return Array.from(ams).sort();
  }, [currentContracts]);

  const statusOptions = useMemo(() => {
    const statuses = new Set();
    currentContracts.forEach((c) => {
      if (c.status && c.status.trim()) statuses.add(c.status.trim());
    });
    return Array.from(statuses).sort();
  }, [currentContracts]);

  const handleUpdateContract = async (contractId, updateData) => {
    try {
      const updated = await api.updateContract(contractId, updateData);
      if (updated.archived && viewMode === "active") {
        setContracts((prev) => prev.filter((c) => c.id !== contractId));
        setArchivedContracts((prev) => [updated, ...prev]);
        toast.info(`"${updated.job_name}" archived (Closed)`);
      } else {
        if (viewMode === "active") {
          setContracts((prev) => prev.map((c) => (c.id === contractId ? updated : c)));
        } else {
          setArchivedContracts((prev) => prev.map((c) => (c.id === contractId ? updated : c)));
        }
      }
    } catch (e) {
      console.error("Failed to update contract:", e);
      toast.error("Failed to update contract");
    }
  };

  const handleUnarchive = async (contractId) => {
    try {
      const restored = await api.unarchiveContract(contractId);
      setArchivedContracts((prev) => prev.filter((c) => c.id !== contractId));
      setContracts((prev) => [restored, ...prev]);
      toast.success(`"${restored.job_name}" restored`);
    } catch (e) {
      console.error("Failed to unarchive contract:", e);
      toast.error("Failed to restore contract");
    }
  };

  const handleDeleteContract = async (contractId) => {
    const contract = archivedContracts.find((c) => c.id === contractId);
    if (!window.confirm(`Delete "${contract?.job_name || "this contract"}" permanently?`)) return;
    try {
      await api.deleteContract(contractId);
      setArchivedContracts((prev) => prev.filter((c) => c.id !== contractId));
      toast.success(`Contract deleted`);
    } catch (e) {
      console.error("Failed to delete contract:", e);
      toast.error("Failed to delete contract");
    }
  };

  const handleAddContract = async () => {
    if (!newContract.job_name.trim()) {
      toast.error("Job Name is required");
      return;
    }
    setSaving(true);
    try {
      const created = await api.createContract({
        ...newContract,
        received: newContract.received || null,
      });
      setContracts((prev) => [created, ...prev]);
      toast.success(`Contract "${created.job_name}" created`);
      setShowAddModal(false);
      setNewContract({ job_number: "", job_name: "", contract_type: "Contract", received: "", status: "Open", notes: "", account_manager: "", customer: "", contact_name: "", contact_email: "" });
    } catch (e) {
      console.error("Failed to create contract:", e);
      toast.error("Failed to create contract");
    } finally {
      setSaving(false);
    }
  };

  const replaceContract = useCallback((u) => setContracts((prev) => prev.map((c) => (c.id === u.id ? u : c))), []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="contracts-page">
      <div className="page-pane gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="contracts-toolbar">
        <SearchBar value={searchQuery} onChange={setSearchQuery}
                   placeholder="Search..." testId="contracts-search-input" />
        <Segmented
          options={[{ key: "active", label: "Active",
                      testId: "contracts-view-active" },
                    { key: "archive", label: "Archived",
                      count: archivedContracts.length || undefined,
                      testId: "contracts-view-archive" }]}
          value={viewMode}
          onChange={setViewMode}
        >
          <span className="mx-0.5 h-4 w-px bg-slate-300" />
          <Pill p={{ key: "contracts-am", label: "AM", options: amOptions.map((a) => ({ value: a, label: a })),
                     value: amFilter === "ALL" ? "" : amFilter, onChange: (v) => setAmFilter(v || "ALL") }} />
          <Pill p={{ key: "contracts-status", label: "Status", options: statusOptions.map((x) => ({ value: x, label: x })),
                     value: statusFilter === "ALL" ? "" : statusFilter, onChange: (v) => setStatusFilter(v || "ALL") }} />
        </Segmented>
        {viewMode === "active" && (
          <Button data-testid="add-contract-button" size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={() => setShowAddModal(true)}>
            <Plus className="h-3.5 w-3.5" /> Contract
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-slate-400 font-medium" data-testid="contracts-count">
            {filteredContracts.length} contract{filteredContracts.length !== 1 ? "s" : ""}
          </span>
          <span className="font-semibold text-green-800" data-testid="contracts-total-value"
                title={viewMode === "active" ? "Total value of the contracts still to close" : "Total value of the archived contracts"}>
            Total: {formatShortMoney(filteredContracts.reduce((t, c) => t + (Number(c.contract_value) || 0), 0))}
          </span>
        </div>
      </div>
      <div className="pane-card border border-slate-200 bg-white">


      {/* Add Contract Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md" data-testid="add-contract-modal">
          <DialogHeader>
            <DialogTitle>Add New Contract</DialogTitle>
            <p className="text-sm text-slate-500">Enter the contract details below.</p>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="job_name">Job Name *</Label>
              <Input
                id="job_name"
                data-testid="add-contract-job-name"
                placeholder="Enter job name"
                value={newContract.job_name}
                onChange={(e) => setNewContract((p) => ({ ...p, job_name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="customer">Customer</Label>
              <Input
                id="customer"
                data-testid="add-contract-customer"
                placeholder="Customer / GC name"
                value={newContract.customer}
                onChange={(e) => setNewContract((p) => ({ ...p, customer: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  data-testid="add-contract-contact-name"
                  placeholder="First Last"
                  value={newContract.contact_name}
                  onChange={(e) => setNewContract((p) => ({ ...p, contact_name: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  data-testid="add-contract-contact-email"
                  placeholder="name@example.com"
                  value={newContract.contact_email}
                  onChange={(e) => setNewContract((p) => ({ ...p, contact_email: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="job_number">Job #</Label>
                <Input
                  id="job_number"
                  data-testid="add-contract-job-number"
                  placeholder="e.g. 12345"
                  value={newContract.job_number}
                  onChange={(e) => setNewContract((p) => ({ ...p, job_number: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="account_manager">Account Manager</Label>
                <Input
                  id="account_manager"
                  data-testid="add-contract-am"
                  placeholder="e.g. BJ"
                  value={newContract.account_manager}
                  onChange={(e) => setNewContract((p) => ({ ...p, account_manager: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select
                  value={newContract.contract_type}
                  onValueChange={(val) => setNewContract((p) => ({ ...p, contract_type: val }))}
                >
                  <SelectTrigger data-testid="add-contract-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={newContract.status}
                  onValueChange={(val) => setNewContract((p) => ({ ...p, status: val }))}
                >
                  <SelectTrigger data-testid="add-contract-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="received">Received Date</Label>
              <Input
                id="received"
                type="date"
                data-testid="add-contract-received"
                value={newContract.received}
                onChange={(e) => setNewContract((p) => ({ ...p, received: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                data-testid="add-contract-notes"
                rows={2}
                placeholder="Optional notes"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#007BFF] resize-none"
                value={newContract.notes}
                onChange={(e) => setNewContract((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} data-testid="add-contract-cancel">
              Cancel
            </Button>
            <Button
              className=""
              onClick={handleAddContract}
              disabled={saving}
              data-testid="add-contract-submit"
            >
              {saving ? "Creating..." : "Create Contract"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center" data-testid="contracts-loading">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">Loading contracts...</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-scroll overflow-y-auto bg-white" data-testid="contracts-table-scroll">
          <ContractsTable
            data={filteredContracts}
            globalFilter={searchQuery}
            onUpdateContract={handleUpdateContract}
            onReplaceContract={replaceContract}
            isArchiveView={viewMode === "archive"}
            onUnarchive={handleUnarchive}
            onDelete={handleDeleteContract}
            onFollowUp={openFollowup}
          />
        </div>
      )}
      </div></div>

      <FollowupDialog
        contract={followupFor}
        preview={followupPreview}
        sending={followupSending}
        onCancel={() => setFollowupFor(null)}
        onSend={sendFollowup}
      />
    </div>
  );
}
